import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

function EHRLiveTest({ practiceId }) {
  // Reference data
  const [providers, setProviders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);

  // Step 1: Push Slots
  const [slotProvider, setSlotProvider] = useState('');
  const [slotDepartment, setSlotDepartment] = useState('');
  const [slotDate, setSlotDate] = useState('');
  const [slotTime, setSlotTime] = useState('09:00');
  const [slotReasonId, setSlotReasonId] = useState('');
  const [slotApptTypeId, setSlotApptTypeId] = useState('');
  const [slotDuration, setSlotDuration] = useState('45');
  const [slotCount, setSlotCount] = useState('1');
  const [pushingSlots, setPushingSlots] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [step1Done, setStep1Done] = useState(false);

  // Step 2: Wait for Webhook
  const [openSlots, setOpenSlots] = useState([]);
  const [polling2, setPolling2] = useState(false);
  const [step2Done, setStep2Done] = useState(false);
  const poll2Ref = useRef(null);
  const initialSlotCountRef = useRef(0);

  // Step 3: Create Patient
  const [ptFirstName, setPtFirstName] = useState('');
  const [ptLastName, setPtLastName] = useState('');
  const [ptDob, setPtDob] = useState('1990-01-01');
  const [ptPhone, setPtPhone] = useState('');
  const [ptEmail, setPtEmail] = useState('');
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [createPatientResult, setCreatePatientResult] = useState(null);
  const [athenaPatientId, setAthenaPatientId] = useState('');
  const [step3Done, setStep3Done] = useState(false);

  // Step 4: Book Slot
  const [selectedSlot, setSelectedSlot] = useState('');
  const [bookingReasonId, setBookingReasonId] = useState('');
  const [bookingSlot, setBookingSlot] = useState(false);
  const [bookResult, setBookResult] = useState(null);
  const [step4Done, setStep4Done] = useState(false);

  // Step 5: Wait for Booking Webhook
  const [polling5, setPolling5] = useState(false);
  const [step5Done, setStep5Done] = useState(false);
  const poll5Ref = useRef(null);

  // Loading / error state for reference data
  const [loadingRef, setLoadingRef] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);

  // Event log
  const [eventLog, setEventLog] = useState([]);
  const logEndRef = useRef(null);

  const addLog = useCallback((action, detail) => {
    setEventLog(prev => [...prev, { time: new Date().toLocaleTimeString(), action, detail }]);
  }, []);

  // Load reference data
  useEffect(() => {
    if (!practiceId) return;
    setLoadingRef(true);
    setLoadErrors([]);
    const errors = [];

    Promise.allSettled([
      api.request(`/practices/${practiceId}/voice/providers`)
        .then(res => {
          const list = (res.data || []).filter(p => p.enabled);
          setProviders(list);
          if (list.length === 0) errors.push('No providers found — add providers in Practice Settings');
        }),
      api.request(`/practices/${practiceId}/voice/departments`)
        .then(res => {
          const list = (res.data || []).filter(d => d.athena_department_id);
          setDepartments(list);
          if (list.length === 0) errors.push('No departments with Athena ID — set athena_department_id in Practice Settings');
        }),
      api.request(`/practices/${practiceId}/ehr/reason-codes`)
        .then(res => {
          const list = (res.data || []).filter(r => r.enabled);
          setReasonCodes(list);
          if (list.length === 0) errors.push('No reason codes — run EHR Sync first');
        }),
      api.request(`/practices/${practiceId}/ehr/appointment-types`)
        .then(res => {
          const list = (res.data || []).filter(t => t.enabled);
          setApptTypes(list);
          if (list.length === 0) errors.push('No appointment types — run EHR Sync first');
        }),
    ]).then(results => {
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const labels = ['Providers', 'Departments', 'Reason Codes', 'Appt Types'];
          errors.push(`${labels[i]} failed: ${r.reason?.message || r.reason}`);
        }
      });
      setLoadErrors(errors);
      setLoadingRef(false);
    });

    // Default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSlotDate(tomorrow.toISOString().split('T')[0]);
  }, [practiceId]);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [eventLog]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (poll2Ref.current) clearInterval(poll2Ref.current);
      if (poll5Ref.current) clearInterval(poll5Ref.current);
    };
  }, []);

  // ─── Step 1: Push Slots ───────────────────────────────────
  const handlePushSlots = async () => {
    if (pushingSlots) return;
    setPushingSlots(true);
    try {
      const count = parseInt(slotCount, 10) || 1;
      const duration = parseInt(slotDuration, 10) || 45;
      const [startH, startM] = slotTime.split(':').map(Number);
      const provider = providers.find(p => String(p.id) === slotProvider);
      const dept = departments.find(d => String(d.id) === slotDepartment);
      const reasonCode = reasonCodes.find(r => String(r.ehr_reason_id) === slotReasonId);

      const slots = [];
      for (let i = 0; i < count; i++) {
        const dt = new Date(`${slotDate}T00:00:00Z`);
        dt.setUTCHours(startH, startM + (i * 60), 0, 0);
        slots.push({
          ID: parseInt(slotReasonId, 10) || 81,
          DateTime: dt.toISOString().replace('T', ' ').replace('.000Z', 'Z'),
          Reason: reasonCode?.reason_text || 'Office Visit',
          Duration: duration,
          AppointmentTypeID: parseInt(slotApptTypeId, 10) || 1,
          Provider: {
            ID: String(provider?.athena_provider_id || '1'),
            IDType: 'EHRID',
            FirstName: provider?.first_name || 'Unknown',
            LastName: provider?.last_name || 'Provider'
          },
          Location: {
            Department: String(dept?.athena_department_id || '21')
          }
        });
      }

      // Capture initial open slot count before push
      const currentSlots = await api.request(`/practices/${practiceId}/ehr/open-slots`).catch(() => ({ data: [] }));
      initialSlotCountRef.current = (currentSlots.data || []).length;

      const res = await api.post(`/practices/${practiceId}/ehr/push-slots`, { slots });
      setPushResult(res.data || res);
      setStep1Done(true);
      addLog('Push Slots', `Pushed ${count} slot(s) → ${JSON.stringify(res.data?.Slots || res.Slots || [])}`);

      // Start polling for Step 2
      setPolling2(true);
      poll2Ref.current = setInterval(async () => {
        try {
          const r = await api.request(`/practices/${practiceId}/ehr/open-slots`);
          const slots = r.data || [];
          setOpenSlots(slots);
          if (slots.length > initialSlotCountRef.current) {
            clearInterval(poll2Ref.current);
            poll2Ref.current = null;
            setPolling2(false);
            setStep2Done(true);
            addLog('Webhook Received', `${slots.length} open slot(s) now in local DB`);
          }
        } catch {}
      }, 10000);
    } catch (err) {
      addLog('Push Slots Error', err.message || String(err));
    }
    setPushingSlots(false);
  };

  // ─── Step 3: Create Patient ───────────────────────────────
  const handleCreatePatient = async () => {
    if (creatingPatient) return;
    setCreatingPatient(true);
    try {
      const dept = departments.find(d => String(d.id) === slotDepartment);
      const res = await api.post(`/practices/${practiceId}/ehr/create-patient`, {
        firstName: ptFirstName,
        lastName: ptLastName,
        dob: ptDob,
        phone: ptPhone,
        email: ptEmail,
        departmentId: String(dept?.athena_department_id || '21')
      });
      setCreatePatientResult(res.data || res);

      // Extract patient ID from response
      const data = res.data || res;
      const pid = data.Patient?.Identifiers?.find(i => i.IDType === 'EHRID')?.ID
        || data.Patient?.Identifiers?.[0]?.ID
        || data.patientid || data.PatientID || data.ID || '';
      setAthenaPatientId(String(pid));
      setStep3Done(true);
      addLog('Create Patient', `${ptFirstName} ${ptLastName} → Athena Patient ID: ${pid}`);
    } catch (err) {
      addLog('Create Patient Error', err.message || String(err));
    }
    setCreatingPatient(false);
  };

  // ─── Step 4: Book Slot ────────────────────────────────────
  const handleBookSlot = async () => {
    if (bookingSlot) return;
    setBookingSlot(true);
    try {
      const dept = departments.find(d => String(d.id) === slotDepartment);
      const res = await api.post(`/practices/${practiceId}/ehr/book-slot`, {
        athenaPatientId: parseInt(athenaPatientId, 10),
        appointmentId: parseInt(selectedSlot, 10),
        reasonId: parseInt(bookingReasonId || slotReasonId, 10),
        departmentId: parseInt(dept?.athena_department_id || 21, 10)
      });
      setBookResult(res.data || res);
      setStep4Done(true);
      addLog('Book Slot', `Patient ${athenaPatientId} booked → ${JSON.stringify(res.data || res)}`);

      // Start polling for Step 5
      setPolling5(true);
      const slotToWatch = selectedSlot;
      poll5Ref.current = setInterval(async () => {
        try {
          const r = await api.request(`/practices/${practiceId}/ehr/open-slots`);
          const currentOpen = r.data || [];
          // If the selected slot is no longer in open slots, it was booked
          const stillOpen = currentOpen.find(s => String(s.athena_appointment_id) === slotToWatch);
          if (!stillOpen) {
            clearInterval(poll5Ref.current);
            poll5Ref.current = null;
            setPolling5(false);
            setStep5Done(true);
            setOpenSlots(currentOpen);
            addLog('Booking Webhook', `Appointment updated — patient assigned, slot no longer open`);
          }
        } catch {}
      }, 10000);
    } catch (err) {
      addLog('Book Slot Error', err.message || String(err));
    }
    setBookingSlot(false);
  };

  // ─── Render ───────────────────────────────────────────────
  const stepStyle = (done, active) => ({
    border: `2px solid ${done ? '#10b981' : active ? '#3b82f6' : '#d1d5db'}`,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    backgroundColor: done ? '#f0fdf4' : active ? '#eff6ff' : '#f9fafb',
    opacity: active || done ? 1 : 0.5,
    transition: 'all 0.3s ease'
  });

  const stepNum = (num, done) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '28px', height: '28px', borderRadius: '50%', marginRight: '10px',
    backgroundColor: done ? '#10b981' : '#3b82f6', color: 'white',
    fontSize: '14px', fontWeight: 'bold'
  });

  const btnStyle = (color, disabled) => ({
    padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: disabled ? 'default' : 'pointer',
    backgroundColor: color, color: 'white', fontWeight: '600', fontSize: '13px',
    opacity: disabled ? 0.5 : 1
  });

  const inputStyle = {
    padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px'
  };

  const selectStyle = {
    padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', backgroundColor: 'white'
  };

  const labelStyle = { fontSize: '12px', color: '#6b7280', fontWeight: '500' };

  if (!practiceId) {
    return (
      <div style={{ padding: '24px', maxWidth: '100%' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>EHR Live Test</h2>
        <p style={{ color: '#6b7280' }}>Select a practice from the sidebar to begin.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>EHR Live Test</h2>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: loadErrors.length ? '12px' : '24px' }}>
        End-to-end test: Push slots → Webhook → Create patient → Book → Webhook
      </p>
      {loadingRef && <p style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '12px' }}>Loading reference data...</p>}
      {loadErrors.length > 0 && (
        <div style={{ padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', marginBottom: '16px', fontSize: '12px', color: '#991b1b' }}>
          {loadErrors.map((err, i) => <div key={i} style={{ marginBottom: i < loadErrors.length - 1 ? '4px' : 0 }}>⚠ {err}</div>)}
        </div>
      )}

      {/* ── Step 1: Push Slots ── */}
      <div style={stepStyle(step1Done, true)}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={stepNum(1, step1Done)}>{step1Done ? '\u2713' : '1'}</span>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Push Open Slots to Athena</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <select value={slotProvider} onChange={e => setSlotProvider(e.target.value)} style={{ ...selectStyle, flex: '1 1 180px' }}>
            <option value="">Provider...</option>
            {providers.map(p => <option key={p.id} value={String(p.id)}>{p.first_name} {p.last_name} (ID:{p.athena_provider_id})</option>)}
          </select>
          <select value={slotDepartment} onChange={e => setSlotDepartment(e.target.value)} style={{ ...selectStyle, flex: '1 1 150px' }}>
            <option value="">Department...</option>
            {departments.map(d => <option key={d.id} value={String(d.id)}>{d.name} (ID:{d.athena_department_id})</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <input type="date" value={slotDate} onChange={e => setSlotDate(e.target.value)} style={{ ...inputStyle, width: '140px' }} />
          <input type="time" value={slotTime} onChange={e => setSlotTime(e.target.value)} style={{ ...inputStyle, width: '100px' }} />
          <select value={slotReasonId} onChange={e => setSlotReasonId(e.target.value)} style={{ ...selectStyle, flex: '1 1 200px' }}>
            <option value="">Reason Code...</option>
            {reasonCodes.map(r => <option key={r.id} value={r.ehr_reason_id}>{r.reason_text} (ID:{r.ehr_reason_id})</option>)}
          </select>
          <select value={slotApptTypeId} onChange={e => setSlotApptTypeId(e.target.value)} style={{ ...selectStyle, flex: '1 1 200px' }}>
            <option value="">Appt Type...</option>
            {apptTypes.map(t => <option key={t.id} value={t.ehr_type_id}>{t.name} (ID:{t.ehr_type_id})</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={labelStyle}>Duration:</label>
          <input type="number" value={slotDuration} onChange={e => setSlotDuration(e.target.value)} style={{ ...inputStyle, width: '60px' }} />
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>min</span>
          <label style={{ ...labelStyle, marginLeft: '8px' }}>Count:</label>
          <input type="number" value={slotCount} onChange={e => setSlotCount(e.target.value)} min="1" max="5" style={{ ...inputStyle, width: '50px' }} />
          <button
            onClick={handlePushSlots}
            disabled={pushingSlots || !slotProvider || !slotDepartment || !slotDate || !slotReasonId}
            style={{ ...btnStyle('#d97706', pushingSlots || !slotProvider || !slotDepartment || !slotDate || !slotReasonId), marginLeft: 'auto' }}
          >
            {pushingSlots ? 'Pushing...' : 'Push Slots'}
          </button>
        </div>
        {pushResult && (
          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fefce8', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace' }}>
            {JSON.stringify(pushResult.Slots || pushResult, null, 2)}
          </div>
        )}
      </div>

      {/* ── Step 2: Wait for Webhook ── */}
      <div style={stepStyle(step2Done, step1Done && !step2Done)}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={stepNum(2, step2Done)}>{step2Done ? '\u2713' : '2'}</span>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Wait for Webhook — Open Slots</span>
          {polling2 && <span style={{ marginLeft: '12px', fontSize: '12px', color: '#3b82f6', animation: 'pulse 2s infinite' }}>Polling every 10s...</span>}
        </div>
        {openSlots.length > 0 ? (
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '4px 8px' }}>Athena ID</th>
                <th style={{ padding: '4px 8px' }}>Date</th>
                <th style={{ padding: '4px 8px' }}>Type</th>
                <th style={{ padding: '4px 8px' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {openSlots.map(s => (
                <tr key={s.athena_appointment_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{s.athena_appointment_id}</td>
                  <td style={{ padding: '4px 8px' }}>{s.appointment_date}</td>
                  <td style={{ padding: '4px 8px' }}>{s.appointment_type}</td>
                  <td style={{ padding: '4px 8px' }}>{s.duration_minutes}min</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
            {step1Done ? 'Waiting for webhook to deliver slots (~2 min)...' : 'Push slots first'}
          </p>
        )}
      </div>

      {/* ── Step 3: Create Patient ── */}
      <div style={stepStyle(step3Done, step2Done)}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={stepNum(3, step3Done)}>{step3Done ? '\u2713' : '3'}</span>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Create Patient on Athena</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <input placeholder="First Name" value={ptFirstName} onChange={e => setPtFirstName(e.target.value)} style={{ ...inputStyle, flex: '1 1 140px' }} disabled={!step2Done} />
          <input placeholder="Last Name" value={ptLastName} onChange={e => setPtLastName(e.target.value)} style={{ ...inputStyle, flex: '1 1 140px' }} disabled={!step2Done} />
          <input type="date" value={ptDob} onChange={e => setPtDob(e.target.value)} style={{ ...inputStyle, width: '140px' }} disabled={!step2Done} />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input placeholder="Phone (e.g., 5551234567)" value={ptPhone} onChange={e => setPtPhone(e.target.value)} style={{ ...inputStyle, flex: '1 1 150px' }} disabled={!step2Done} />
          <input placeholder="Email" value={ptEmail} onChange={e => setPtEmail(e.target.value)} style={{ ...inputStyle, flex: '1 1 180px' }} disabled={!step2Done} />
          <button
            onClick={handleCreatePatient}
            disabled={creatingPatient || !step2Done || !ptFirstName || !ptLastName}
            style={{ ...btnStyle('#7c3aed', creatingPatient || !step2Done || !ptFirstName || !ptLastName), marginLeft: 'auto' }}
          >
            {creatingPatient ? 'Creating...' : 'Create Patient'}
          </button>
        </div>
        {step3Done && athenaPatientId && (
          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f5f3ff', borderRadius: '6px', fontSize: '13px' }}>
            Athena Patient ID: <strong>{athenaPatientId}</strong>
          </div>
        )}
        {createPatientResult && (
          <details style={{ marginTop: '4px', fontSize: '12px' }}>
            <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Full response</summary>
            <pre style={{ fontSize: '11px', maxHeight: '150px', overflow: 'auto', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '4px' }}>
              {JSON.stringify(createPatientResult, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* ── Step 4: Book Slot ── */}
      <div style={stepStyle(step4Done, step3Done)}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={stepNum(4, step4Done)}>{step4Done ? '\u2713' : '4'}</span>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Book Patient into Slot</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedSlot} onChange={e => setSelectedSlot(e.target.value)} style={{ ...selectStyle, flex: '1 1 300px' }} disabled={!step3Done}>
            <option value="">Select open slot...</option>
            {openSlots.map(s => (
              <option key={s.athena_appointment_id} value={String(s.athena_appointment_id)}>
                {s.appointment_date?.split('T')[0]} {s.appointment_date?.split('T')[1]?.substring(0,5) || ''} — {s.appointment_type} [ID:{s.athena_appointment_id}]
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={labelStyle}>Patient:</label>
            <input value={athenaPatientId} onChange={e => setAthenaPatientId(e.target.value)} style={{ ...inputStyle, width: '80px' }} disabled={!step3Done} />
          </div>
          <select value={bookingReasonId || slotReasonId} onChange={e => setBookingReasonId(e.target.value)} style={{ ...selectStyle, flex: '1 1 180px' }} disabled={!step3Done}>
            <option value="">Reason...</option>
            {reasonCodes.map(r => <option key={r.id} value={r.ehr_reason_id}>{r.reason_text} ({r.ehr_reason_id})</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button
            onClick={handleBookSlot}
            disabled={bookingSlot || !step3Done || !selectedSlot || !athenaPatientId}
            style={btnStyle('#059669', bookingSlot || !step3Done || !selectedSlot || !athenaPatientId)}
          >
            {bookingSlot ? 'Booking...' : 'Book Appointment'}
          </button>
        </div>
        {bookResult && (
          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#ecfdf5', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace' }}>
            {JSON.stringify(bookResult, null, 2)}
          </div>
        )}
      </div>

      {/* ── Step 5: Wait for Booking Webhook ── */}
      <div style={stepStyle(step5Done, step4Done && !step5Done)}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={stepNum(5, step5Done)}>{step5Done ? '\u2713' : '5'}</span>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Wait for Booking Webhook</span>
          {polling5 && <span style={{ marginLeft: '12px', fontSize: '12px', color: '#3b82f6' }}>Polling every 10s...</span>}
        </div>
        {step5Done ? (
          <div style={{ padding: '12px', backgroundColor: '#ecfdf5', borderRadius: '8px', fontSize: '13px' }}>
            <strong>Booking confirmed!</strong> The appointment has been updated with the patient. The local DB is now in sync with Athena.
          </div>
        ) : step4Done ? (
          <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
            Waiting for Athena to send booking webhook (~2 min)...
          </p>
        ) : (
          <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Book a patient first</p>
        )}
      </div>

      {/* ── Event Log ── */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '8px' }}>
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', fontSize: '13px' }}>
          Event Log
        </div>
        <div style={{ maxHeight: '200px', overflow: 'auto', padding: '8px 16px' }}>
          {eventLog.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>No events yet</p>
          ) : (
            eventLog.map((e, i) => (
              <div key={i} style={{ fontSize: '12px', marginBottom: '4px', display: 'flex', gap: '8px' }}>
                <span style={{ color: '#9ca3af', fontFamily: 'monospace', flexShrink: 0 }}>{e.time}</span>
                <span style={{ fontWeight: '600', color: '#374151', flexShrink: 0 }}>{e.action}</span>
                <span style={{ color: '#6b7280', wordBreak: 'break-all' }}>{e.detail}</span>
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export default EHRLiveTest;
